import { Link } from 'react-router-dom'

export default function AuthenticationGuide() {
  return (
    <div>
      <h1>Authentication</h1>
      <p>
        Every request to the Chosen API is authenticated with an API key sent as
        a Bearer token in the <code>Authorization</code> header. Keys begin with
        the prefix <code>cho_</code>.
      </p>

      <h2>Get a key</h2>
      <ol>
        <li>
          Sign in to{' '}
          <a href="https://hire.chosenhq.com">hire.chosenhq.com</a>.
        </li>
        <li>
          Open <strong>Settings → Developers</strong>.
        </li>
        <li>
          Click <strong>Create key</strong>, give it a name, and pick a role.
          The role controls which endpoints the key can call.
        </li>
        <li>
          Copy the key when it appears. It is shown <em>only once</em> — store
          it in a password manager or secret store.
        </li>
      </ol>

      <h2>Send a request</h2>
      <pre>
        <code>{`curl https://hire.chosenhq.com/api/v1/jobs \\
  -H "Authorization: Bearer cho_..."`}</code>
      </pre>

      <h2>Key scopes (RBAC)</h2>
      <p>
        Each key is bound to a single role inside a single organization. The
        permissions on that role determine which endpoints the key can call.
        Three system roles ship out of the box:
      </p>
      <ul>
        <li>
          <strong>Owner</strong> — full access. Holds the <code>*</code>{' '}
          wildcard permission.
        </li>
        <li>
          <strong>Admin</strong> — read and write to candidates (incl. notes),
          jobs, applications, feedback, scheduling, sourcing, candidate emails,
          uploads, exports, and integrations. Can manage org members, custom
          roles, API keys, and OAuth sessions. Can read analytics.
        </li>
        <li>
          <strong>Viewer</strong> — read-only access to candidates, jobs,
          applications, feedback, scheduling, sourcing, and analytics.
        </li>
      </ul>
      <p>
        You can also create <strong>custom roles</strong> with hand-picked
        permissions. When creating an API key, you cannot grant a role with
        more permissions than your own user has — least-privilege is enforced
        at issuance.
      </p>

      <h2>Rotate &amp; revoke</h2>
      <p>
        Keys can be revoked at any time from <strong>Settings → Developers</strong>{' '}
        or programmatically (see <code>DELETE /api/v1/orgs/{'{'}org_id{'}'}/api-keys/{'{'}key_id{'}'}</code>).
        Revocation is immediate.
      </p>
      <p>
        For automated rotation, use the API-key meta-endpoints to create a new
        key, deploy it, then revoke the old one.
      </p>

      <h2>Org pinning</h2>
      <p>
        A key is pinned to one organization. Sending an{' '}
        <code>X-Org-Id</code> header that doesn't match the key's org returns{' '}
        <code>403</code>. Likewise, impersonation headers are rejected when
        authenticated by API key — keys cannot impersonate users.
      </p>

      <h2>What's next</h2>
      <ul>
        <li>
          <Link to="/reference">Browse the API reference</Link>
        </li>
        <li>
          <Link to="/guides/errors">Error codes</Link>
        </li>
        <li>
          <Link to="/guides/rate-limits">Rate limits</Link>
        </li>
      </ul>
    </div>
  )
}
